import { defineStore } from 'pinia'
import { CoreV2Service } from '@/services/core-v2-service'
import type { CcxtMarket } from '@/services/types'
import type { LeverageResponse, MarketInfo } from '@/services/eye-v2-service/types'
import type { ApiKeyType } from '@/services/api-keys-v2-service/types'
import { computed, ref } from 'vue'
import { useRequestFactory } from './factories/requestFactory'
import type { MarketType } from '@/utils/types'

export interface UserPosition {
  profileNum: number
  symbol: string
  side: string
  leverage: number
  contracts: number
  entryPrice: number
  liquidationPrice: number
}

export const useCoreStore = defineStore('core', () => {
  const { loading, errors, requestHandler } = useRequestFactory<string>()
  const markets = ref<Record<string, CcxtMarket>>({})
  const tickers = ref<Record<string, MarketInfo>>({})
  const leverages = ref<Record<string, LeverageResponse>>({})
  // const positions = ref<Record<number, Record<string, any>>>({}) // eslint-disable-line
  const positionsPerExchange = ref<Record<string, Record<number, any>>>({}) // eslint-disable-line
  const orders = ref<Record<number, Record<string, any>>>({}) // eslint-disable-line
  const fetchAllPosesTs = ref<number | null>(null)

  // const addPositionAmount = (
  //   exchange: string,
  //   symbol: string,
  //   profile: number,
  //   amountToAdd: number,
  // ) => {
  //   if (positions.value[profile]?.[exchange]?.[symbol]) {
  //     positions.value[profile][exchange][symbol]['amount'] += amountToAdd
  //   } else {
  //     positions.value[profile] ??= {}
  //     positions.value[profile][exchange] ??= {}
  //     positions.value[profile][exchange][symbol] = {
  //       info: {},
  //       id: null,
  //       symbol: symbol,
  //       timestamp: timestamp,
  //       datetime: datetime,
  //       isolated: marginMode,
  //       hedged: false,
  //       side: side,
  //       contracts: contracts,
  //       contractSize: contractSize,
  //       entryPrice: entryPrice,
  //       markPrice: markPrice,
  //       notional: notional,
  //       leverage: leverage,
  //       collateral: collateral,

  //     }
  //   }
  // }

  const positions = computed(() => {
    const result: Record<string, Record<string, UserPosition[]>> = {}

    Object.entries(positionsPerExchange.value).forEach(([exchange, positionsByProfile]) => {
      Object.entries(positionsByProfile).forEach(([profileNum, positionData]) => {
        if (!result[profileNum]) {
          result[profileNum] = {}
        }
        result[profileNum][exchange] = positionData
      })
    })

    return result
  })

  const parsePositions = (exchanges: string[], symbols: string[]) => {
    // eslint-disable-next-line
    const result = [] as Record<string, any>[]
    Object.entries(positions.value).forEach(([profileNum, exPoses]) => {
      Object.entries(exPoses).forEach(([exName, poses]) => {
        if (!exchanges.includes(exName)) {
          return
        }
        // eslint-disable-next-line
        poses.forEach((x: any) => {
          if (!symbols.includes(x['symbol'])) {
            return
          }
          if (x['contracts'] == 0) {
            return
          }
          result.push({
            exchange: exName,
            profileNum: profileNum,
            ...x,
          })
        })
      })
    })
    result.sort((a, b) => a.profileNum - b.profileNum)
    return result
  }

  const fetchMarket = async (actionId: string, exchange: string, symbol: string) => {
    return await requestHandler(actionId, async () => {
      const req = await CoreV2Service.fetchMarket(exchange, symbol.replace('/', '_'))
      markets.value[actionId] = req.data
      return req.data
    })
  }

  const fetchContracts = async (
    actionId: string,
    exchange: string,
    asset: string,
    marketType: string = 'swap',
  ) => {
    return await requestHandler(actionId, async () => {
      const req = await CoreV2Service.fetchContracts(exchange, asset, marketType)
      return req.data
    })
  }

  const fetchTicker = async (actionId: string, exchange: string, symbol: string) => {
    return await requestHandler(actionId, async () => {
      const req = await CoreV2Service.fetchTicker(exchange, symbol.replace('/', '_'))
      tickers.value[actionId] = req.data
      return req.data
    })
  }

  const fetchLeverage = async (actionId: string, exchange: string, symbol: string) => {
    return await requestHandler(actionId, async () => {
      const req = await CoreV2Service.fetchLeverage(exchange, symbol.replace('/', '_'))
      leverages.value[actionId] = req.data
      return req.data
    })
  }

  // const fetchPositions = async (
  //   actionId: string,
  //   exchange: string,
  //   profileNum: number,
  //   keyType: ApiKeyType,
  // ) => {
  //   return await requestHandler(actionId, async () => {
  //     const req = await CoreV2Service.fetchPositions(exchange, profileNum, keyType)
  //     positions.value[profileNum] ??= {}
  //     positions.value[profileNum][exchange] = req.data
  //     return req.data
  //   })
  // }
  const fetchAllPositions = async (actionId: string, exchange: string, keyType: ApiKeyType) => {
    // const ts = +new Date()
    // if (fetchAllPosesTs.value && ts - fetchAllPosesTs.value < 1) {
    // return positions.value
    // }
    // const newData: Record<number, Record<string, any>> = {} // eslint-disable-line
    return await requestHandler(actionId, async () => {
      const req = await CoreV2Service.fetchAllPositions(exchange, keyType)
      fetchAllPosesTs.value = +new Date()
      const newData: Record<number, Record<string, any>> = {}
      Object.entries(req.data).forEach(([profileNum, value]) => {
        const idx = profileNum as unknown as number
        newData[idx] = value
      })
      positionsPerExchange.value[exchange] = newData
      // positions.value = newData
      return req.data
    })
  }

  const fetchAllOrders = async (
    actionId: string,
    exchange: string,
    keyType: ApiKeyType,
    marketType: MarketType,
    asset: string,
  ) => {
    return await requestHandler(actionId, async () => {
      const req = await CoreV2Service.fetchAllOrders(exchange, keyType, marketType, asset)

      Object.entries(req.data).forEach(([profileNum, value]) => {
        const idx = profileNum as unknown as number
        orders.value[idx] = {}
        orders.value[idx][exchange] = value
      })

      return req.data
    })
  }

  // const fetchOrders = async (
  //   actionId: string,
  //   exchange: string,
  //   profileNum: number,
  //   keyType: ApiKeyType,
  //   marketType: MarketType,
  //   asset: string,
  // ) => {
  //   return await requestHandler(actionId, async () => {
  //     const req = await CoreV2Service.fetchOrders(exchange, profileNum, keyType, marketType, asset)
  //     orders.value[profileNum] = {}
  //     orders.value[profileNum][exchange] = req.data
  //     return req.data
  //   })
  // }

  const fetchLimits = async (
    actionId: string,
    exchange: string,
    marketType: MarketType,
    asset: string,
  ) => {
    return await requestHandler(actionId, async () => {
      const req = await CoreV2Service.fetchLimits(exchange, marketType, asset)
      return req.data
    })
  }

  const fetchPoolDeveloper = async (actionId: string, poolAddress: string, chain: string) => {
    return await requestHandler(actionId, async () => {
      const req = await CoreV2Service.fetchPoolDev(poolAddress, chain)
      return req.data
    })
  }

  return {
    loading,
    errors,
    markets,
    tickers,
    positions,
    orders,
    fetchLimits,
    fetchMarket,
    fetchAllOrders,
    fetchContracts,
    fetchTicker,
    fetchLeverage,
    // fetchPositions,
    fetchAllPositions,
    // fetchOrders,
    parsePositions,
    fetchPoolDeveloper,
  }
})
